// Screen — Finance reconciliation (ledger ↔ BC drift).
//
// A read-only cross-account reconciliation view: the server compares each
// account's remaining ledger balance against the BC-reported available balance
// and returns the per-account drift plus a rollup summary. This screen shows
// the summary tiles on top (with a stale badge when the BC cache is stale),
// then the per-account drift table with color-coded flag chips.
//
//   GET /admin/finance/reconciliation
//     -> { stale, rows: [{ account_id, ledger_remaining, bc_available,
//          currency, drift, flags[] }],
//          summary: { accounts, matched, missing_in_bc, currency_mismatch,
//          total_ledger_remaining, total_drift } }
//
// MONEY: ledger_remaining / bc_available / drift / totals are EXACT decimal
// strings from the backend and are rendered VERBATIM via MoneyCell — never
// parsed to a float. This screen writes NOTHING; it only displays what the
// backend (the source of truth) computed.

import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  DataTable,
  MoneyCell,
  StatCard,
  ToneBadge,
  type Column,
  type Tone,
} from "@/components/nexus";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// --- Real backend shapes -----------------------------------------------------
interface ReconciliationRow {
  account_id: string;
  ledger_remaining: string;
  bc_available: string;
  currency: string;
  drift: string;
  flags: string[];
}
interface ReconciliationSummary {
  accounts: number;
  matched: number;
  missing_in_bc: number;
  currency_mismatch: number;
  total_ledger_remaining: string;
  total_drift: string;
}
interface Reconciliation {
  stale: boolean;
  rows: ReconciliationRow[];
  summary: ReconciliationSummary;
}

// A money string is "zero" when it is 0 / 0.0 / 0.00 (with an optional sign).
function isZeroMoney(s: string): boolean {
  return /^-?0(\.0{1,2})?$/.test((s ?? "").trim());
}

// Flag chip tone: the two structural flags are amber; anything else is neutral.
function flagTone(flag: string): Tone {
  return flag === "missing_in_bc" || flag === "currency_mismatch"
    ? "warn"
    : "na";
}

export function ReconciliationPage() {
  const [data, setData] = useState<Reconciliation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get<Reconciliation>(
          "/admin/finance/reconciliation",
        );
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(errMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = data?.rows ?? [];
  const summary = data?.summary;

  const columns: Column<ReconciliationRow>[] = [
    {
      key: "account_id",
      header: "Account",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text tabular-nums">
          {r.account_id}
        </span>
      ),
    },
    {
      key: "ledger_remaining",
      header: "Ledger remaining",
      align: "right",
      cell: (r) => <MoneyCell value={r.ledger_remaining} currency={r.currency} />,
    },
    {
      key: "bc_available",
      header: "BC available",
      align: "right",
      cell: (r) =>
        r.flags.includes("missing_in_bc") ? (
          <span className="text-nx-text-3">—</span>
        ) : (
          <MoneyCell value={r.bc_available} currency={r.currency} tone="muted" />
        ),
    },
    {
      key: "currency",
      header: "Currency",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2">{r.currency}</span>
      ),
    },
    {
      key: "drift",
      header: "Drift",
      align: "right",
      cell: (r) => {
        // A non-zero drift is the whole point of the screen — emphasize it.
        const zero = isZeroMoney(r.drift);
        return (
          <MoneyCell
            value={r.drift}
            currency={r.currency}
            tone={zero ? "muted" : "debit"}
            className={zero ? undefined : "font-semibold"}
          />
        );
      },
    },
    {
      key: "flags",
      header: "Flags",
      cell: (r) =>
        r.flags.length === 0 ? (
          <ToneBadge tone="credit">matched</ToneBadge>
        ) : (
          <div className="flex flex-wrap gap-1">
            {r.flags.map((f) => (
              <ToneBadge key={f} tone={flagTone(f)}>
                {f.replace(/_/g, " ")}
              </ToneBadge>
            ))}
          </div>
        ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">
            Finance reconciliation
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
            Per-account drift between each account&apos;s remaining ledger
            balance and its BC-reported available balance. Read-only — the
            backend computes every figure.
          </p>
        </div>
        {data?.stale && (
          <ToneBadge tone="stale" className="mt-1">
            Stale — showing cached BC data
          </ToneBadge>
        )}
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load the reconciliation</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <div className="grid gap-5">
          {summary && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard label="Accounts" value={summary.accounts} />
              <StatCard
                label="Matched"
                value={summary.matched}
                tone="credit"
                meta={
                  <span className="text-nx-text-3">
                    of {summary.accounts} reconciled
                  </span>
                }
              />
              <StatCard
                label="Missing in BC"
                value={summary.missing_in_bc}
                tone={summary.missing_in_bc > 0 ? "debit" : "muted"}
              />
              <StatCard
                label="Currency mismatch"
                value={summary.currency_mismatch}
                tone={summary.currency_mismatch > 0 ? "debit" : "muted"}
              />
              <StatCard
                label="Total ledger remaining"
                value={`$${summary.total_ledger_remaining}`}
              />
              <StatCard
                label="Total drift"
                value={`$${summary.total_drift}`}
                tone={isZeroMoney(summary.total_drift) ? "muted" : "debit"}
              />
            </div>
          )}

          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.account_id}
            empty="No reconcilable accounts yet."
          />
        </div>
      )}
    </AdminShell>
  );
}
